import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MarkdownRenderer from '../shared/components/MarkdownRenderer';

// Same convention as the rest of the app (auth/api).
// Without this prefix, the relative URL resolves against the FE dev
// server (:3000) and 404s instead of hitting the backend (:5050).
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';

interface OpenDataCredit {
  attribution: string;
  licence: string;
  licenceUrl: string | null;
}

/**
 * "Open data we use": the credits the night-train timetables' open-data
 * licences ask for (spec §7.5), and nothing more: no feed descriptions, no
 * sources that ask for no credit (backend: dataSources in
 * routes/publicNightTrains.js). It replaced the public "Data sources" page on
 * 2026-10-01 (Paulo: less of a recipe for competitors); the map's corner and
 * the leg sheet's "Open data" link here. Kept out of the Terms' markdown, which
 * is the contract text. Each attribution line is shown exactly as written:
 * Austria's and Germany's licences require their words.
 * Docs: docs/product/night-trains/README.md.
 */
const OpenDataCredits: React.FC = () => {
  const { hash } = useLocation();
  const [credits, setCredits] = useState<OpenDataCredit[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/public/night-trains/sources`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => { if (!cancelled) setCredits(Array.isArray(data.sources) ? data.sources : []); })
      .catch(() => { if (!cancelled) setCredits([]); });
    return () => { cancelled = true; };
  }, []);

  // Opened from a link to #open-data: bring the section into view once it has its content.
  useEffect(() => {
    if (credits && hash === '#open-data') document.getElementById('open-data')?.scrollIntoView();
  }, [credits, hash]);

  if (!credits || credits.length === 0) return null;
  return (
    <section id="open-data" className="mt-10 pt-6 border-t border-gray-200 text-sm text-gray-600">
      <h2 className="text-lg font-semibold mb-2 text-gray-800">Open data we use</h2>
      <p className="mb-3">
        Jody's night-train timetables are built from open railway data, which we combine, check and adapt.
        These sources are credited under their licences:
      </p>
      <ul className="space-y-1">
        {credits.map((c) => (
          <li key={c.attribution}>
            {c.attribution}
            {c.licence && (
              <span className="text-gray-500">
                {' · '}
                {c.licenceUrl ? (
                  <a href={c.licenceUrl} target="_blank" rel="noopener noreferrer" className="underline">{c.licence}</a>
                ) : (
                  c.licence
                )}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

const TermsAndConditionsPage: React.FC = () => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await fetch(`${API_BASE}/content/terms-and-conditions.md`);
        if (!response.ok) {
          throw new Error('Failed to load terms and conditions content');
        }
        const markdownContent = await response.text();
        setContent(markdownContent);
      } catch (err) {
        console.error('Error loading terms and conditions:', err);
        setError('Failed to load terms and conditions content');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-jody-paper py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading terms and conditions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-jody-paper py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-jody-paper py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-sm rounded-lg p-8">
        <MarkdownRenderer content={content} />
        <OpenDataCredits />
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;

